// Product Media Grouping Script
// This script groups product media by variant options and shows/hides media based on selected variants

class ProductMediaGrouping {
  constructor() {
    this.productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    this.optionsToWatch = [];
    this.colorImageRanges = {};
    this.mediaIDToColor = {};
    this.mediaItems = [];
    this.mediaGallery = null;
    
    this.init();
  }

  async init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    } catch (error) {
      console.error("Error initializing ProductMediaGrouping:", error);
    }
  }

  async setup() {
    try {
      // Get product data
      const product = await this.getProductData();
      if (!product) return;

      // Get settings from meta tag or data attribute
      this.optionsToWatch = this.getOptionsToWatch();
      
      // Setup media grouping
      this.setupMediaGrouping(product);
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Initial display
      this.showVariantImage();
      
    } catch (error) {
      console.error("Error in setup:", error);
    }
  }

  async getProductData() {
    try {
      const productUrl = `${window.Shopify.routes.root}products/${this.productHandle}.js`;
      const response = await fetch(productUrl);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error("Error fetching product data:", error);
      return null;
    }
  }

  getOptionsToWatch() {
    // Try to get from meta tag first
    const metaTag = document.querySelector('meta[name="media-grouping-option"]');
    if (metaTag) {
      return metaTag.content.split(',').map(opt => opt.trim());
    }
    
    // Fallback to data attribute
    const dataAttr = document.querySelector('[data-media-grouping-option]');
    if (dataAttr) {
      return dataAttr.dataset.mediaGroupingOption.split(',').map(opt => opt.trim());
    }
    
    // Default fallback
    return ['Color', 'Colour'];
  }

  setupMediaGrouping(product) {
    const colorOption = product.options.find(option => 
      this.optionsToWatch.includes(option.name)
    );
    
    if (!colorOption) {
      console.log("No matching option found for media grouping");
      return;
    }

    // Build color-image ranges
    product.variants.forEach(variant => {
      const color = variant.options[colorOption.position - 1];
      if (variant.featured_media) {
        this.colorImageRanges[color] = [variant.featured_media.position, Infinity];
      }
    });

    // Sort and adjust ranges
    const sortedEntries = Object.entries(this.colorImageRanges).sort((a, b) => a[1][0] - b[1][0]);
    sortedEntries.forEach(([color], index) => {
      if (sortedEntries[index + 1]) {
        this.colorImageRanges[color][1] = sortedEntries[index + 1][1][0];
      }
    });

    // Map media to colors
    product.media.forEach(media => {
      const color = sortedEntries.find(([, [start, end]]) => 
        media.position >= start && media.position < end
      )?.[0];
      if (color) this.mediaIDToColor[media.id] = color;
    });

    // Setup media gallery
    this.setupMediaGallery();
  }

  setupMediaGallery() {
    this.mediaGallery = document.querySelector('media-gallery');
    if (!this.mediaGallery) {
      console.log("Media gallery not found");
      return;
    }

    this.mediaItems = Array.from(this.mediaGallery.querySelectorAll('li[data-media-id], li[data-target]'))
      .filter(li => {
        const attr = li.getAttribute('data-media-id') || li.getAttribute('data-target');
        const mediaID = attr?.split('-').pop();
        return mediaID && this.mediaIDToColor[mediaID];
      });

    // Add data attributes
    this.mediaItems.forEach(li => {
      const attr = li.getAttribute('data-media-id') || li.getAttribute('data-target');
      const mediaID = attr?.split('-').pop();
      li.setAttribute('data-media-group', this.mediaIDToColor[mediaID]);
    });
  }

  setupEventListeners() {
    // Listen for variant changes
    this.listenForVariantChanges();
    
    // Listen for URL changes (for variant switching)
    this.listenForUrlChanges();
  }

  listenForVariantChanges() {
    // Listen to variant selector changes
    const variantSelectors = [
      'variant-selects select',
      'fieldset input[type="radio"]',
      'fieldset input[type="checkbox"]',
      '.product-variant-id',
      '[data-variant-selector]'
    ];

    variantSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.addEventListener('change', () => this.showVariantImage());
        element.addEventListener('click', () => this.showVariantImage());
      });
    });

    // Listen for custom variant change events
    document.addEventListener('variant:change', () => this.showVariantImage());
    document.addEventListener('variant:changed', () => this.showVariantImage());
  }

  listenForUrlChanges() {
    // Listen for URL hash changes
    window.addEventListener('hashchange', () => this.showVariantImage());
    
    // Listen for popstate events
    window.addEventListener('popstate', () => this.showVariantImage());
  }

  showVariantImage() {
    if (!this.mediaItems.length) return;

    // Get selected variant
    const selectedColor = this.getSelectedVariantColor();
    
    if (!selectedColor) {
      // Show all media if no variant is selected
      this.mediaItems.forEach(li => li.classList.remove('hide-media'));
      return;
    }

    // Hide/show media based on selected variant
    this.mediaItems.forEach(li => {
      const shouldHide = li.getAttribute('data-media-group') !== selectedColor;
      li.classList.toggle('hide-media', shouldHide);
    });

    // Update slider components
    if (this.mediaGallery) {
      this.mediaGallery.querySelectorAll('slider-component').forEach(slider => {
        if (slider.initPages) slider.initPages();
      });
    }
  }

  getSelectedVariantColor() {
    // Try different selectors to find selected variant
    const selectors = [
      'variant-selects select option[selected]',
      'fieldset input:checked',
      '.product-variant-id option[selected]',
      '[data-variant-selector] option[selected]'
    ];

    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      for (const el of elements) {
        const value = el.value;
        if (this.colorImageRanges[value]) {
          return value;
        }
      }
    }

    return null;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductMediaGrouping());
} else {
  new ProductMediaGrouping();
} 